import { Box, Divider, Grid, List, ListItem, styled, Typography } from '@mui/material';
import React from 'react';
import { FooterColumn } from '../../interfaces/footer-columns.interface';
import Colors from '../../config/colors';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    navigateWithScrollToTop,
    navigateWithAnchor,
} from '../../Utils/navigation-with-scroll.util';

interface FooterListRendererProps {
    isScreenMobile?: boolean;
    list: FooterColumn;
}

const FooterListTitle = styled(Typography)`
    font-family: 'Poppins Bold';
    @media (min-width: 0px) {
        color: ${Colors.purple};
        font-size: 18px;
    }
    @media (min-width: 900px) {
        color: ${Colors.black};
    }
    @media (min-width: 1200px) {
        font-size: 20px;
    }
`;

const FooterListItem = styled(Typography)`
    font-family: 'Poppins Light';
    @media (min-width: 0px) {
        font-size: 16px;
    }
    @media (min-width: 900px) {
        font-size: 18px;
    }
`;

const FooterList = ({ list, isScreenMobile }: FooterListRendererProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleItemClick = (item: string | { text: string; route: string }) => {
        if (typeof item === 'object' && 'route' in item) {
            const route = item.route;

            // Check if route has an anchor (hash)
            if (route.includes('#')) {
                const [path, anchor] = route.split('#');
                navigateWithAnchor(navigate, path, anchor);
            } else {
                // Regular navigation with scroll to top
                navigateWithScrollToTop(navigate, route);
            }
        }
    };

    return (
        <>
            {isScreenMobile && (
                <Divider
                    sx={{
                        borderTop: `2px solid ${Colors.purple}`,
                        width: '20%',
                        margin: '10px 0 20px 0',
                    }}
                />
            )}
            {!isScreenMobile && (
                <List>
                    <FooterListTitle variant="h6">{t(list.title)}</FooterListTitle>
                    {list.items.map((item, i) => {
                        const isFooterItem = typeof item === 'object' && 'text' in item;
                        const displayText = isFooterItem
                            ? (item as { text: string; route: string }).text
                            : t(item as string);
                        const isClickable = isFooterItem;

                        return (
                            <ListItem key={i} disableGutters>
                                <Box
                                    onClick={
                                        isClickable
                                            ? () =>
                                                  handleItemClick(
                                                      item as { text: string; route: string }
                                                  )
                                            : undefined
                                    }
                                    sx={{
                                        cursor: isClickable ? 'pointer' : 'default',
                                        textDecoration: 'underline',
                                        textDecorationColor: 'transparent',
                                        '&:hover': {
                                            textDecorationColor: 'inherit',
                                        },
                                    }}
                                >
                                    <FooterListItem>{displayText}</FooterListItem>
                                </Box>
                            </ListItem>
                        );
                    })}
                </List>
            )}
            {isScreenMobile && (
                <>
                    <Box>
                        <FooterListTitle variant="h6">{t(list.title)}</FooterListTitle>
                    </Box>
                    <Grid container spacing={1} mb={2}>
                        {list.items.map((item, i) => {
                            const isFooterItem = typeof item === 'object' && 'text' in item;
                            const displayText = isFooterItem
                                ? (item as { text: string; route: string }).text
                                : t(item as string);
                            const isClickable = isFooterItem;

                            return (
                                <Grid item xs={3} key={i}>
                                    <Box
                                        onClick={
                                            isClickable
                                                ? () =>
                                                      handleItemClick(
                                                          item as { text: string; route: string }
                                                      )
                                                : undefined
                                        }
                                        sx={{
                                            cursor: isClickable ? 'pointer' : 'default',
                                            textDecoration: 'underline',
                                            textDecorationColor: 'transparent',
                                            '&:hover': {
                                                textDecorationColor: 'inherit',
                                            },
                                        }}
                                    >
                                        <FooterListItem>{displayText}</FooterListItem>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                </>
            )}
        </>
    );
};

export default FooterList;
